<template>
	<section class="section-app" :class="status">
		<section-head
			title="Select A Cabin"
			description="You may directly select your cabin or filter by cabin type!"
		/>

		<button class="multiselect-blur" ref="blured"></button>

		<multiselect
			placeholder="Filter by cabin type:"
			label="name" track-by="id"
			:value="value"
			:options="sortedOptions"
			:option-height="112"
			:max-height="650"
			:searchable="false"
			:multiple="true"
			:close-on-select="false"
			:clear-on-select="false"
			:hide-selected="false"
			:preserve-search="true"
			@select="updateOptionsSelect"
			@remove="removeOptionsSelect"
			@close="handleMultiselectClose"
		>
			<template
				slot="singleLabel"
				slot-scope="props"
				>

				<span class="option__description">
					<span class="option__title">{{ props.option.name }}</span>
				</span>
			</template>

			<template
				slot="option"
				slot-scope="props"
				>
					<div
						:class="[
							'option',
							props.option.isInner ? 'option--child':'',
							isChangingOptions ? 'animation-class' : '',
						]"
					>
						<span class="option__color"
							:style="`background-color: ${props.option.color};`"
						></span>

						<div class="option__image"
							:class="props.option.unavailable ? 'sold':''"
							:style="`background-image: url(${props.option.image})`"
						>
							<div
								class="option__sold"
								v-if="props.option.unavailable"
							>
								<h3>Sold Out</h3>
							</div>

							<div class="option__title">
								<h3>{{ props.option.name }}</h3>
							</div>
							<span class="option__price" v-if="props.option.price">
								<p>From: ${{ formatNumber(props.option.price) }}</p>
							</span>
						</div>

						<div class="option__description">
							<p>
								{{ props.option.description }}
							</p>

							<span class="option__element"
								:style="`background-image: url(${assetImage}/images/check-mark.svg)`"
							>
								<span></span>
							</span>
						</div>
					</div><!-- /.option -->
			</template>
		</multiselect>

	<div class="room" :class="isZoomed ? 'zoomed':''">
		<div class="room__head"
			data-simplebar data-simplebar-auto-hide="false"
		>
		<div class="room__nav" v-if="availableCabins.length > 0">
			<div
				v-for="cabin in availableCabins"
				class="room__link"
				:class="`${active === cabin.id ? 'is-active':''} ${!cabin.is_reserved ? 'available' : 'sold-out'}`"
				@click="handleCabinSelection(cabin.id)"
			>
				<button>
					{{ cabin.number }}
				</button>
			</div>
		</div>

		<div class="room_no-cabins"
			v-else
		>
			<h5>There are no available cabins for this deck.</h5>
		</div>
	</div><!-- /.room__head -->

	<div class="room__content">
			<div class="room__body"
				v-for="cabin in availableCabins"
				v-if="active === cabin.id"
				data-simplebar data-simplebar-auto-hide="false"
				>
				<div class="room__info">
					<button class="btn-remove"
					@click="handleCabinRemove"
					>
					<img :src="'/images/plus-button.svg' | assetUrl" alt="">
				</button>

				<div class="room__info-head">
					<h3>
						{{ cabin.name }}

						<template v-for="type in cabin.type">
							<span>|</span>

							{{ type }}
						</template>
					</h3>
				</div>

				<div class="room__info-image">
					<img :src="cabin.image" alt="">

					<span>
						${{ formatNumber(cabin.price/product.passengers) }} Per Person
					</span>
				</div>

				<div class="room__info-body">
					<p>
						{{ cabin.description }}
					</p>
				</div>

				<div class="room__info-foot">
					<button class="btn btn-beige" @click.prevent="addToCart(cabin)" v-if="!cabin.is_reserved">
						BOOK NOW
					</button>

					<button class="btn btn-gray" v-else>
						Sold Out
					</button>
				</div>
			</div>
		</div>

		<div class="room__aside">
			<div data-simplebar data-simplebar-auto-hide="false">
				<div class="room__image">
					<svg width="100%" height="100%" style="background: rgba(51, 51, 51, .5);" :viewBox="`0 0 ${selectedDeck.cabinsImage.width} ${selectedDeck.cabinsImage.height}`">
						<defs>
							<clipPath id="multiple-1">
								<template v-for="cabin in availableCabins">
									<polygon
									fill="#f00"
									@click="handleCabinSelection(cabin.id)"
									:points="cabin.path"
									/>
								</template>
							</clipPath>
						</defs>
						<g>
							<image clip-path="url(#multiple-1)"
								:width="`${selectedDeck.cabinsImage.width}`"
								:height="`${selectedDeck.cabinsImage.height}`"
								x="0" y="0"
								:xlink:href="`${selectedDeck.cabinsImage.image}`"
							/>

						</g>
					</svg>
					<svg width="100%" height="100%" :viewBox="`0 0 ${selectedDeck.cabinsImage.width} ${selectedDeck.cabinsImage.height}`">
						<template v-for="cabin in availableCabins">
							<polygon
							fill="transparent"
							@click="handleCabinSelection(cabin.id)"
							:points="cabin.path"
							:class="active === cabin.id ? 'active':''"
							/>
						</template>
					</svg>
					<img :src="`${selectedDeck.cabinsImage.image}`" alt="">
				</div>
			</div>
		</div>
	</div>
</div>

	<div class="section__foot">
		<button
			class="btn btn-gray"
			@click="handlePrevClick"
		>
			Back
		</button>
	</div>

	<div class="section__notice" v-if="texts && texts.cabins_bottom_text">
		<p>{{texts.cabins_bottom_text}}</p>
	</div>
</section>
</template>

<script>
/**
* External Dependencies
*/
import 'simplebar';
import 'simplebar/dist/simplebar.css';
import 'vue-multiselect/dist/vue-multiselect.min.css';
import _, {
	get,
	intersection,
	isEmpty,
	isEqual,
	orderBy,
} from 'lodash';
import Multiselect from 'vue-multiselect'
import { mapGetters, mapActions } from 'vuex';

/**
* Internal Dependencies
*/
import SectionHead from '../components/SectionHead.vue';
import numberFormat from '../mixins/number-format';
import texts from '../mixins/texts';

export default {
	/**
	* Component Name.
	*
	* @type {String}
	*/
	name: 'cruise-cabin',

	mixins: [numberFormat, texts],

	data(){
		return{
			status: null,
			isChangingOptions: false,
			isZoomed: true,
			active: null,
			value: [],
			options: [],
			isMain: false,
			assetImage: crb_app.theme_url
		}
	},

	components:{
		SectionHead,
		Multiselect
	},

	computed:{
		...mapGetters({
			'product':'getProduct',
			'decks':'getDecks',
		}),

		sortedOptions: function() {
		   return this.options.map(option => {
				return Object.assign(option, {
					inner_types: _.orderBy(option.inner_types, 'name', 'asc')
				});
			  });
		},

		selectedDeck(){
			const selectedDeck = this.decks.filter(deck => deck.id === this.product.deck)
			return selectedDeck[0]
		},

		availableCabins(){
			if ( !this.selectedDeck.cabins ) {
				return [];
			}

			const availableCabins = this.selectedDeck.cabins.filter(cabin => cabin.occupancy >= this.product.passengers);
			const names = this.selectedTypes

			if (isEmpty(names)) {
				return orderBy(availableCabins, 'number', 'asc');
			}

			return orderBy(availableCabins.filter(cabin => !isEmpty(intersection(cabin.type, names)) ), 'number', 'asc');
		},

		selectedCabin() {
			return this.selectedDeck.cabins.filter(cabin => cabin.id === this.product.cabin)[0];
		},

		selectedTypes() {
			if (this.value.length === 1) {
				return [ this.value[0].name ];
			}

			return this.value.filter(val => val.isInner)
				.map(val => val.name);
		},

		cabinTypes() {
			return _.reduce( this.selectedCabin.type, (typeString, type) => {
				typeString = ! typeString ? type : `${typeString} | ${type}`;

				return typeString;
			}, '' );
		}

	},

	methods:{
		...mapActions([
			'setProductEntry',
			'resetProductEntry',
			'completeStep',
			'resetSteps',
		]),

		handlePrevClick() {
			this.$router.push({name:'cruise.deck'})
		},

		handleCabinSelection(id) {
			this.active = id;

			this.setProductEntry({
				entry: 'cabin',
				fields: this.active
			});

			this.completeStep({
				step: 'cabin'
			});
		},

		handleCabinRemove(){
			this.active = null;

			this.resetProductEntry({
				entry: ['cabin'],
			});

			this.resetSteps({
				steps: ['cabin'],
			});
		},

		changeOptions(value){
			const optionValue = this.options;

			const newOptionValue = optionValue.filter(option => option.name === value.name)
			const innerOpt = newOptionValue.concat(newOptionValue[0].inner_types);

			this.options = innerOpt;
		},

		updateOptionsSelect (selectedOption, id) {
			if (selectedOption.unavailable) {
				return false;
			}
			setTimeout(() => {
				this.isChangingOptions = true;
			}, 1);

			this.value.push(selectedOption)

			if (!this.isMain) {
				this.changeOptions(selectedOption)
			} else {
				this.$refs.blured.focus();
			}

			this.isMain = true;
		},

		handleMultiselectClose(){
			if (!this.isMain) {
				this.isChangingOptions = false;
			}
		},

		removeOptionsSelect (removedOption, id) {
			const sortedOptions = this.value.filter(singleValue => singleValue.name !== removedOption.name);

			this.value = sortedOptions

			if (removedOption.hasChild || this.value.length < 1) {
				this.value = [];

				this.options = this.$store.getters.getTypesByDeckId(this.product.deck)[0].cabinTypes;
				this.isMain = false;
				this.isChangingOptions = false;
				this.active = null;

				this.resetProductEntry({
					entry: ['cabin'],
				})
			}
		},


		addToCart(cabin){
			this.status = 'generating-order';

			jQuery.ajax( {
				url: crb_app.ajax_url,
				method: 'POST',
				data: {
					action: 'crb_proceed_to_room',
					room_id: cabin.post_id,
					deck: this.selectedDeck.title,
					deck_id: this.selectedDeck.id,
					room_type: this.cabinTypes,
					occupancy: this.product.passengers,
					heading: crb_app.app_data.heading,
					date: crb_app.app_data.date,
					path: crb_app.app_data.path,
					special_fee_name:crb_app.app_data.special_fee_name,
					special_fee_amount:crb_app.app_data.special_fee_amount,
					special_fee_restrict:crb_app.app_data.special_fee_restrict,
				}
			} )
			.done( (response) => {
				cabin.gratuity = response.room.gratuity_tax;
				cabin.port_taxes = response.room.port_tax
				cabin.extra_traveler_fees = response.room.extra_traveler_fees;
				cabin.total = response.room.total_taxes

				this.$router.push({name:'cruise.order'});
				this.status = null;
			} )
			.fail( error => this.status = null )
		},
	},

	mounted(){
		if (this.product.cabin !== null) {
			this.active = this.product.cabin
		}

		const selectedDeck = this.$store.getters.getTypesByDeckId(this.product.deck);

		this.options = selectedDeck[0].cabinTypes
	}
}
</script>

<style>
.multiselect .option { transition: max-height .6s, transform .5s; }
.multiselect .option--child { max-height: 0 !important;}
.multiselect .option--child.animation-class { max-height: 500px !important; }

.multiselect-blur { position: absolute; opacity: 0;}
.multiselect .multiselect__option { min-height: 1px;  }

.simplebar-track { width: 10px; z-index: 5; }
.simplebar-scrollbar { width: 10px; right: 0; }
.simplebar-scrollbar:before { border-radius: 0; background: #4a4a4a; }

.multiselect { z-index: 30; margin-bottom: 20px; }
.multiselect .multiselect__tags-wrap { position: relative; display: flex; flex-wrap: wrap; padding-left: 5px; }
.multiselect .multiselect__tags-wrap:before {  content: 'Filter by cabin type:'; font-weight: 700; margin-right: 10px; margin-top: 3px; font-size: 16px; line-height: 20px; }
.multiselect .multiselect__tags { border-radius: 0; padding-right: 80px; min-height: 50px; box-shadow: 0 2px 4px rgba(0, 0, 0, .5); display: flex; align-items: center; padding-top: 1px; margin-bottom: 8px; }
.multiselect .multiselect__single { font-weight: 700; margin-bottom: 0; position: relative; bottom: -1px;  }

.multiselect .multiselect__select { width: 80px; height: 100%; }
.multiselect .multiselect__select:before { width: 0; height: 0; top: 64%;  border-width: 10px 10px 0 10px; border-color: #4a4a4a transparent transparent;  }
.multiselect .multiselect__option { padding: 0; }
.multiselect .multiselect__content-wrapper {  box-shadow: 0 2px 4px rgba(0, 0, 0, .5); border-radius: 0; border: 0; }
.multiselect .multiselect__content { width: 100%;}
.multiselect .multiselect__element {  overflow: hidden; background: transparent !important; }
.multiselect .multiselect__element .option { border-bottom: 2px solid #979797; }
.multiselect .multiselect__tag { background: transparent; color: #aa8735; font-size: 16px; font-weight: 700; border-radius: 0; padding: 5px 10px 5px 10px; margin: 0; }
.multiselect .multiselect__tag:first-child { padding-left: 0; }
.multiselect .multiselect__tag + .multiselect__tag:before { content: ''; position: absolute; top: 4px; bottom: 4px; left: 0; width: 2px; background: #4a4a4a;  }
.multiselect .multiselect__tag-icon { background: #aa8735; display: none; }
.multiselect .multiselect__tag-icon:after { color: #fff;  }


.multiselect .multiselect__option.multiselect__option--highlight  { background: #fff; color: #4a4a4a;  }
.multiselect .multiselect__option.multiselect__option--highlight:after { display: none; }
.multiselect .multiselect__option.multiselect__option--highlight .option__element { background-color: #13373d; }
.multiselect .multiselect__option.multiselect__option--highlight:not(.multiselect__option--selected) .option--child .option__element { background-color: #979797;  }
.multiselect .multiselect__option.multiselect__option--highlight .option__element span { background-color: #fff;  }
.multiselect .multiselect__option.multiselect__option--highlight .option__element span:before { background: #fff;  }

.multiselect .multiselect__option--selected { background: #fff; font-weight: inherit; }
.multiselect .multiselect__option--selected:after { display: none; }
.multiselect .multiselect__option--selected.multiselect__option--highlight {  }
.multiselect .multiselect__option--selected .option__element span:before { display: none; }
.multiselect .multiselect__option--selected .option--child .option__element span{ display: none; }
.multiselect .multiselect__option--selected .option--child .option__element span  {  }
.multiselect .multiselect__option:not(.multiselect__option--selected ) .option--child .option__element { background-image: none !important;  }

.multiselect .option { white-space: normal; width: 100%; display: flex; padding-left: 15px; background: #fff; }
.multiselect .option .option__image { position: relative; width: 135px; height: 110px; background-size: cover; }
.multiselect .option .option__image:before { content: ''; background: linear-gradient(135deg, rgba(0,0,0,1) 0%,rgba(0,0,0,1) 1%,rgba(0,0,0,0) 32%,rgba(0,0,0,0) 56%);  }
.multiselect .option .option__sold { position: absolute; top: 0; left: 0; width: 100%; height: 100%; background: rgba(74, 74, 74, .7); z-index: 10;  }
.multiselect .option .option__sold h3 { content: 'Sold Out'; font-size: 20px; display: flex; align-items: center; justify-content: center; width: auto; height: auto;  position: absolute; top: 0; left: 0; right: 0; bottom: 0; margin: auto; transform: rotate(-30deg); z-index: 15; color: #fff; }
.multiselect .option .option__price,
.multiselect .option .option__title { color: #fff; position: absolute; left: 0; width: 100%;  padding: 5px 9px; }
.multiselect .option .option__title { font-size: 20px; top: 0; }
.multiselect .option .option__price { bottom: 0; font-size: 14px; }
.multiselect .option .option__color { position: absolute; top: 0; left: 0; width: 15px; bottom: 0; }
.multiselect .option .option__element { position: absolute; top: 50%; right: 20px; margin-top: -20px; width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; border: 1px solid #979797; border-radius: 8px; background-size: 20px; background-repeat: no-repeat; background-position: center; }
.multiselect .option .option__element > span { position: relative; width: 20px; height: 4px; border-radius: 2px; background: #979797;  }
.multiselect .option .option__element > span:before { content: ''; position: absolute; width: 4px; height: 20px; left: 50%; top: -8px; margin-left: -2px; border-radius: 2px;  background: #979797; transition: opacity .4s; opacity: 1; }

.multiselect .option .option__description { font-size: 14px; position: relative;  width: calc(100% - 135px); padding: 10px 100px 10px 20px; }
.multiselect .option .option__description p { margin-bottom: 0;  }

.multiselect .option.option--child { background: #4a4a4a; color: #fff; }
.multiselect .option.option--child .option__title { font-size: 16px; }
.multiselect .option.option--child .option__element { background-color: #4a4a4a;  }
.multiselect .option.option--child .option__element span { display: none; }
.multiselect .option:not(.option--child) .option__element { background-image: none !important;  }

.btn-remove { border-radius: 0; border: 0; background: transparent; appearance: none; width: 40px; height: 40px; line-height: 40px; text-align: center; }

.room:not(.zoomed) .vue-scrollbar__area { margin-top: 0 !important; transition: none !important;  }

.room { display: flex; justify-content: space-between;}
.room .room__image { position: relative; text-align: center; }
.room .room__image svg { position: absolute; top: 0; left: 0; width: 100%; height: 100%; }
.room .room__image svg > polygon.active { fill: rgba(137, 210, 191, .4); cursor: pointer;  }
.room .room__image svg > polygon:hover { fill: rgba(137, 210, 191, .4); cursor: pointer; }
.room .room__image img { height: 100%; width: auto;  }
.room.zoomed .room__image img { width: 100%; height: auto; }

.room .room__controls { position: absolute; top: 20px; right: 20px; width: 110px; }
.room .room__controls button { display: block; width: 100%; appearance: none; border-radius: 16px; border: 1px solid #9b9b9b; color: #9b9b9b; font-weight: 700; box-shadow: 0 2px 4px rgba(0, 0, 0, .5); background: #fff; margin-bottom: 20px; height: 33px; text-align: center; white-space: nowrap; transition: border .4s, color .4s; }
.room .room__controls button:hover { border-color: #13373d; color: #13373d;  }


.room .room__link { position: relative; z-index: 1; font-size: 16px; text-align: center; width: 100%; font-weight: 700; color: #9b9b9b; }
.room .room__link button { position: relative; width: 100%; appearance: none; border: 0; border-radius: 0; background: #fff;  border-left: 10px solid #89d2bf; text-decoration: none; display: block; color: inherit; padding: 15px 10px; transition: color .4s, border .4s, background .4s; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; font-weight: 700; }
.room .room__link button:before { content: ''; position: absolute; bottom: 0; left: 0; bottom: 0; right: 0; height: 1px; background: #d3d3d3; transition: background .4s; }
.room .room__link.is-active button { color: #fff; background: #13373d; border-left-color: #13373d; }
.room .room__link.is-active button:before {  background: #13373d;  }
.room .room__link:not(.is-active):hover button { background: #a27c2c;  border-left-color: #a27c2c; color: #fff;}
.room .room__link:not(.is-active):hover button:before { background: #a27c2c; }

.room .room__head { width: 250px; height: 650px; background: #fff; position: relative;  box-shadow: 0 0 4px rgba(0, 0, 0, .5); }
.room .room__nav { list-style: none; }
.room .room__nav:before { content: ''; top: 0; left: 0; width: 10px; bottom: 0; position: absolute; background: #89d2bf; }

.room .room__content  { position: relative;  background: #fff; width: calc(100% - 270px); margin-left: 20px; box-shadow: 0 0 4px rgba(0, 0, 0, .5);}
.room .room__body { max-height: 685px; position: absolute; top: 0; left: 0; width: 100%; z-index: 15;  }
.room .room__aside > div { height: 650px; }

.room .room__info { position: relative; padding: 0 20px 20px; background: #fff; }
.room .room__info .btn-remove { position: absolute; top: 0; right: 0;  }

.room .room__info-head { padding: 14px 20px 11px; text-align: center; }
.room .room__info-head h3 { font-size: 16px; color: #a27c2c; }
.room .room__info-head h3 span { color: #4a4a4a;  }

.room .room__info-image { position: relative; margin-bottom: 13px; }
.room .room__info-image:after { content: ''; position: absolute; top: 0; left: 0; width: 100%; height: 100%; background: linear-gradient(135deg, rgba(0,0,0,1) 0%,rgba(0,0,0,1) 1%,rgba(0,0,0,0) 32%,rgba(0,0,0,0) 56%); }
.room .room__info-image span {  display: inline-block; font-size: 24px; font-weight: 700; letter-spacing: .34px; color: #13373d; }
.room .room__info-image img { width: 100%; height: auto; }
.room .room__info-image img + span { position: absolute; bottom: 0; left: 0;padding: 5px 10px; color: #fff; z-index: 5;  }

.room .room__info-body { font-size: 14px; line-height: 1.71; font-weight: 300; color: #808184; }
.room .room__info-foot { padding-top: 20px; text-align: center; }

.room .room_no-cabins { padding: 10px; }

.fade-enter-active, .fade-leave-active {
  transition: opacity 5s; opacity: 0;
}
.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}

.room .room__link.sold-out { cursor: pointer; }
.room .room__link.sold-out button { background: #fff; border-left: 10px solid #89d2bf; pointer-events: none; overflow: hidden; }
.room .room__link.sold-out button:before { background: rgba(0, 0, 0,.6); width: 100%; height: 100%; }
.room .room__link.sold-out button:after { content: 'Sold Out'; width: 100%; height: 100%; transform: rotate(-16deg);  position: absolute; top: 0; left: 0; display: flex; align-items: center; justify-content: center; color: #fff; font-weight: 700; z-index: 2; }
.room .room__link.sold-out:hover button {  }
.room .room__link.sold-out:hover button:before  {  }
.room .room__link.sold-out.is-active button { color: #fff; background: #13373d; border-left-color: #13373d; }


@media(max-width: 767px){
	.multiselect .multiselect__tags { padding-right: 60px; }
	.multiselect .multiselect__select { width: 50px; }
	.multiselect .option .option__description { padding-right: 80px;  }

	.room { display: block;  }
	.room .room__content { width: 100%; margin: 0; }
	.room .room__nav { width: 100%; }
	.room .room__head { width: 100%; height: auto; margin-bottom: 20px;  }
	.room .room__aside > div { height: 400px;  }
	.room .room__body { max-height: 434px;  }
}

@media(max-width: 480px){
	.multiselect .multiselect__tags-wrap:before { display: block; width: 100%; margin-right: 0;  }

	.multiselect .option  { min-height: 140px; }
	.multiselect .option .option__image { width: 100px; height: 80px; }
	.multiselect .option .option__title h3 { font-size: 14px;  }
	.multiselect .option .option__description { position: static; width: calc(100% - 100px); padding: 10px; }
	.multiselect .option .option__element { top: auto; bottom: 10px; right: auto; left: 45px;  }
}
</style>



// WEBPACK FOOTER //
// src/pages/CruiseCabin.vue